import React, { useEffect, useMemo, useState } from 'react';
import { procedimientosAPI, contactosAPI, participantesAPI } from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from './ui/command';
import { useToast } from '../hooks/use-toast';
import { Plus, Pencil, Trash2, GitBranch, Download, Scale, ChevronRight, Check, ChevronsUpDown } from 'lucide-react';

const ESTADOS = ['En trámite', 'Con sentencia', 'Firme', 'Archivado'];

const emptyForm = {
  tipo: '', numero: '', organo: '', organo_id: null, instancia: '',
  estado: '', nig: '', fecha: '', notas: '', deriva_de_id: null,
};

export const ProcedimientosJudiciales = ({ expedienteId, historicoTexto, canEdit = true, onOrganoAsignado }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({ tipos: [], instancias: [], instancia_por_tipo: {} });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Buscador de órgano/juzgado (contactos tipo organismo)
  const [organoOpen, setOrganoOpen] = useState(false);
  const [organoSearch, setOrganoSearch] = useState('');
  const [organoResultados, setOrganoResultados] = useState([]);
  const [organoLoading, setOrganoLoading] = useState(false);
  // Organismos ya asignados al expediente (participantes tipo organismo)
  const [asignados, setAsignados] = useState([]);

  const load = async () => {
    try {
      const [cfg, list] = await Promise.all([
        procedimientosAPI.config(),
        procedimientosAPI.list(expedienteId),
      ]);
      setConfig(cfg.data || { tipos: [], instancias: [], instancia_por_tipo: {} });
      setItems(list.data || []);
    } catch (e) {
      console.error('Error cargando procedimientos', e);
    } finally {
      setLoading(false);
    }
  };

  const loadAsignados = async () => {
    try {
      const r = await participantesAPI.getByExpediente(expedienteId);
      const arr = r.data?.data || r.data || [];
      const orgs = arr
        .filter((p) => p.organismo_id)
        .map((p) => ({ id: p.organismo_id, nombre: p.nombre || p.organismo_nombre || `Organismo ${p.organismo_id}` }));
      // dedup por id
      const seen = new Set(); const dedup = [];
      orgs.forEach((o) => { if (!seen.has(o.id)) { seen.add(o.id); dedup.push(o); } });
      setAsignados(dedup);
    } catch { setAsignados([]); }
  };

  useEffect(() => {
    if (!expedienteId) return;
    load();
    loadAsignados();
  }, [expedienteId]);

  // Búsqueda por palabras en servidor (debounce) al abrir el buscador de órgano
  useEffect(() => {
    if (!organoOpen) return;
    setOrganoLoading(true);
    const q = organoSearch.trim();
    const t = setTimeout(async () => {
      try {
        const r = await contactosAPI.getAll({ tipo_entidad: 'organismo', search: q || undefined, limit: 30 });
        setOrganoResultados(r.data?.data || r.data || []);
      } catch { setOrganoResultados([]); }
      finally { setOrganoLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [organoSearch, organoOpen]);

  const selOrgano = (o) => {
    setForm((f) => ({ ...f, organo_id: o.id, organo: o.razon_social || o.nombre || f.organo }));
    setOrganoOpen(false);
  };

  const byId = useMemo(() => {
    const m = {}; items.forEach((p) => { m[p.id] = p; }); return m;
  }, [items]);

  const cadena = (p) => {
    // "Verbal 1/2026 › Imp.Costas 1/2026 › (actual)"
    const chain = []; let cur = p; const seen = new Set();
    while (cur && cur.deriva_de_id && byId[cur.deriva_de_id] && !seen.has(cur.deriva_de_id)) {
      seen.add(cur.deriva_de_id);
      const par = byId[cur.deriva_de_id];
      chain.unshift(`${par.tipo}${par.numero ? ' ' + par.numero : ''}`);
      cur = par;
    }
    return chain;
  };

  const openNew = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      tipo: p.tipo || '', numero: p.numero || '', organo: p.organo || '',
      organo_id: p.organo_id || null, instancia: p.instancia || '',
      estado: p.estado || '', nig: p.nig || '', fecha: p.fecha ? p.fecha.slice(0, 10) : '',
      notas: p.notas || '', deriva_de_id: p.deriva_de_id || null,
    });
    setDialogOpen(true);
  };

  const onTipoChange = (tipo) => {
    setForm((f) => ({ ...f, tipo, instancia: f.instancia || config.instancia_por_tipo?.[tipo] || '' }));
  };

  const save = async () => {
    if (!form.tipo || !form.tipo.trim()) {
      toast({ title: 'Falta el tipo', description: 'Selecciona o escribe el tipo de procedimiento', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingId) await procedimientosAPI.update(editingId, form);
      else await procedimientosAPI.create(expedienteId, form);
      // Si el órgano elegido no estaba asignado al expediente, añadirlo como organismo asignado
      if (form.organo_id && !asignados.some((a) => String(a.id) === String(form.organo_id))) {
        try {
          await participantesAPI.add({
            expediente_id: parseInt(expedienteId, 10),
            organismo_id: form.organo_id,
            tipo_participante: 'organismo',
            rol: 'organismo_asignado',
          });
          await loadAsignados();
          if (onOrganoAsignado) onOrganoAsignado();
        } catch (e2) {
          // no bloquea el guardado del procedimiento
          console.warn('No se pudo asignar el organismo al expediente', e2);
        }
      }
      toast({ title: editingId ? 'Procedimiento actualizado' : 'Procedimiento añadido' });
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const borrar = async (p) => {
    if (!window.confirm(`¿Borrar "${p.tipo}${p.numero ? ' ' + p.numero : ''}"?`)) return;
    try {
      await procedimientosAPI.remove(p.id);
      toast({ title: 'Procedimiento borrado' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo borrar', variant: 'destructive' });
    }
  };

  const importar = async () => {
    try {
      const r = await procedimientosAPI.importarHistorico(expedienteId);
      toast({ title: 'Importado del histórico', description: `${r.data?.creados || 0} procedimiento(s) creado(s). Revisa el tipo y el órgano.` });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo importar', variant: 'destructive' });
    }
  };

  // opciones "deriva de" (todos menos el que se edita)
  const derivaOpts = items.filter((p) => p.id !== editingId);

  return (
    <div data-testid="procedimientos-judiciales">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Scale className="w-4 h-4 text-blue-600" /> Procedimientos judiciales
        </h3>
        {canEdit && (
          <div className="flex gap-2">
            {historicoTexto && historicoTexto.trim() && (
              <Button size="sm" variant="outline" onClick={importar} data-testid="importar-historico-btn">
                <Download className="w-4 h-4 mr-1" /> Importar del histórico
              </Button>
            )}
            <Button size="sm" onClick={openNew} data-testid="add-procedimiento-btn">
              <Plus className="w-4 h-4 mr-1" /> Añadir procedimiento
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Cargando…</p>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-500 border border-dashed rounded-lg p-4">
          No hay procedimientos registrados.
          {historicoTexto && historicoTexto.trim() && (
            <> Puedes <button className="text-blue-600 underline" onClick={importar}>importarlos del histórico</button> y ajustarlos.</>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => {
            const ch = cadena(p);
            return (
              <div key={p.id} className="border rounded-lg p-3 flex items-start justify-between gap-3 bg-white" data-testid={`procedimiento-row-${p.id}`}>
                <div className="min-w-0">
                  {ch.length > 0 && (
                    <div className="text-xs text-gray-400 flex items-center flex-wrap gap-1 mb-1">
                      <GitBranch className="w-3 h-3" />
                      {ch.map((c, i) => (
                        <span key={i} className="flex items-center gap-1">
                          {i > 0 && <ChevronRight className="w-3 h-3" />}{c}
                        </span>
                      ))}
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{p.tipo}</span>
                    {p.numero && <Badge className="bg-blue-100 text-blue-800">{p.numero}</Badge>}
                    {p.estado && <Badge variant="outline" className="text-xs">{p.estado}</Badge>}
                    {p.instancia && <span className="text-xs text-gray-400">· {p.instancia}</span>}
                  </div>
                  <div className="text-sm text-gray-600 mt-0.5">
                    {p.organo || <span className="text-gray-400">Órgano sin especificar</span>}
                    {p.nig && <span className="text-gray-400"> · NIG {p.nig}</span>}
                  </div>
                  {p.notas && <div className="text-xs text-gray-500 mt-1">{p.notas}</div>}
                </div>
                {canEdit && (
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-procedimiento-${p.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => borrar(p)} data-testid={`delete-procedimiento-${p.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar procedimiento' : 'Nuevo procedimiento judicial'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo *</Label>
              <Select value={config.tipos.includes(form.tipo) ? form.tipo : (form.tipo ? '__otro__' : '')} onValueChange={(v) => onTipoChange(v === '__otro__' ? '' : v)}>
                <SelectTrigger data-testid="proc-tipo-select"><SelectValue placeholder="Selecciona tipo" /></SelectTrigger>
                <SelectContent>
                  {config.tipos.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  <SelectItem value="__otro__">Otro (escribir)…</SelectItem>
                </SelectContent>
              </Select>
              {(!config.tipos.includes(form.tipo)) && (
                <Input className="mt-2" placeholder="Escribe el tipo tal cual lo pone el juzgado" value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })} data-testid="proc-tipo-libre" />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Número</Label>
                <Input placeholder="Ej: 1/2026" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="proc-numero" />
              </div>
              <div>
                <Label>Instancia</Label>
                <Select value={form.instancia || ''} onValueChange={(v) => setForm({ ...form, instancia: v })}>
                  <SelectTrigger data-testid="proc-instancia-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {config.instancias.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Órgano / Juzgado</Label>
              <Popover open={organoOpen} onOpenChange={(o) => { setOrganoOpen(o); if (o) setOrganoSearch(''); }}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="proc-organo-combobox">
                    <span className="truncate">{form.organo || 'Buscar organismo / juzgado…'}</span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Escribe para buscar (Girona, Audiencia, Supremo…)" value={organoSearch} onValueChange={setOrganoSearch} data-testid="proc-organo-search" />
                    <CommandList>
                      {asignados.filter((a) => !organoSearch.trim() || (a.nombre || '').toLowerCase().includes(organoSearch.trim().toLowerCase())).length > 0 && (
                        <CommandGroup heading="Asignados a este expediente">
                          {asignados
                            .filter((a) => !organoSearch.trim() || (a.nombre || '').toLowerCase().includes(organoSearch.trim().toLowerCase()))
                            .map((a) => (
                              <CommandItem key={`asig-${a.id}`} value={`asig-${a.id}`} onSelect={() => selOrgano(a)} data-testid={`proc-organo-asignado-${a.id}`}>
                                <Check className={`mr-2 h-4 w-4 ${String(form.organo_id) === String(a.id) ? 'opacity-100' : 'opacity-0'}`} />
                                {a.nombre}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      )}
                      {organoLoading ? (
                        <div className="p-3 text-sm text-gray-400">Buscando…</div>
                      ) : (
                        <>
                          <CommandEmpty>Sin resultados. Escríbelo manualmente abajo.</CommandEmpty>
                          <CommandGroup heading="Todos los organismos">
                            {organoResultados.map((o) => (
                              <CommandItem key={o.id} value={String(o.id)} onSelect={() => selOrgano(o)} data-testid={`proc-organo-opt-${o.id}`}>
                                <Check className={`mr-2 h-4 w-4 ${String(form.organo_id) === String(o.id) ? 'opacity-100' : 'opacity-0'}`} />
                                {o.razon_social || o.nombre}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Input className="mt-2" placeholder="…o escríbelo manualmente (Audiencia Provincial de Girona)" value={form.organo}
                onChange={(e) => setForm({ ...form, organo_id: null, organo: e.target.value })} data-testid="proc-organo-texto" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Estado</Label>
                <Select value={form.estado || ''} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger data-testid="proc-estado-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="proc-fecha" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>NIG</Label>
                <Input value={form.nig} onChange={(e) => setForm({ ...form, nig: e.target.value })} data-testid="proc-nig" />
              </div>
              <div>
                <Label>Deriva de</Label>
                <Select value={form.deriva_de_id ? String(form.deriva_de_id) : '__none__'} onValueChange={(v) => setForm({ ...form, deriva_de_id: v === '__none__' ? null : Number(v) })}>
                  <SelectTrigger data-testid="proc-derivade-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Ninguno (principal) —</SelectItem>
                    {derivaOpts.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.tipo}{p.numero ? ' ' + p.numero : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="proc-notas" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="proc-guardar-btn">{saving ? 'Guardando…' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcedimientosJudiciales;
